import type { GradeLinkView } from "../types";

export type TdsMatchStatus = "exact" | "nameInFile" | "fileNameInGrade" | "close" | "pickOne" | "noMatch" | "notPdf";

export interface MatchableGrade {
  gradeId: number;
  supplier: string;
  tradename: string;
}

export interface TdsMatch {
  status: TdsMatchStatus;
  candidates: MatchableGrade[];
}

const FILLER_WORDS = new Set(["tds", "datasheet", "data", "sheet", "technical", "ds", "spec", "rev", "final", "copy"]);
const YEAR_WORD = /^(19|20)\d\d$/;
const VERSION_WORD = /^v\d+$/;
const SHORTEST_NAME_TO_FIND_IN_FILE = 3;
const SHORTEST_NAME_FOR_CLOSE_MATCH = 5;
const CLOSE_MATCH_ALLOWED_SHARE = 0.15;

export function uniqueGrades(gradeLinks: GradeLinkView[]): MatchableGrade[] {
  const gradesById = new Map<number, MatchableGrade>();
  for (const gradeLink of gradeLinks) {
    if (!gradesById.has(gradeLink.gradeId)) {
      gradesById.set(gradeLink.gradeId, {
        gradeId: gradeLink.gradeId,
        supplier: gradeLink.supplier,
        tradename: gradeLink.tradename,
      });
    }
  }
  return [...gradesById.values()].sort((first, second) => first.tradename.localeCompare(second.tradename));
}

function wordsOf(text: string): string[] {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim().split(" ").filter(Boolean);
}

function fileNameKey(fileName: string): string {
  return wordsOf(fileName.replace(/\.pdf$/i, ""))
    .filter((word) => !FILLER_WORDS.has(word) && !YEAR_WORD.test(word) && !VERSION_WORD.test(word))
    .join("");
}

function editDistance(first: string, second: string): number {
  const distances = Array.from({ length: second.length + 1 }, (_, index) => index);
  for (let firstIndex = 1; firstIndex <= first.length; firstIndex++) {
    let diagonal = distances[0];
    distances[0] = firstIndex;
    for (let secondIndex = 1; secondIndex <= second.length; secondIndex++) {
      const above = distances[secondIndex];
      const isSameLetter = first[firstIndex - 1] === second[secondIndex - 1];
      distances[secondIndex] = Math.min(above + 1, distances[secondIndex - 1] + 1, diagonal + (isSameLetter ? 0 : 1));
      diagonal = above;
    }
  }
  return distances[second.length];
}

function narrowBySupplier(grades: MatchableGrade[], fileKey: string): MatchableGrade[] {
  if (grades.length < 2) return grades;
  const gradesWithSupplierInFile = grades.filter((grade) => {
    const supplierFirstWord = wordsOf(grade.supplier)[0];
    return supplierFirstWord !== undefined && fileKey.includes(supplierFirstWord);
  });
  return gradesWithSupplierInFile.length ? gradesWithSupplierInFile : grades;
}

function singleOrPickOne(status: TdsMatchStatus, candidates: MatchableGrade[]): TdsMatch {
  return { status: candidates.length === 1 ? status : "pickOne", candidates };
}

export function matchTdsFile(fileName: string, grades: MatchableGrade[]): TdsMatch {
  if (!/\.pdf$/i.test(fileName)) return { status: "notPdf", candidates: [] };
  const fileKey = fileNameKey(fileName);
  if (!fileKey) return { status: "noMatch", candidates: [] };
  const gradeKeys = grades.map((grade) => ({ grade, key: wordsOf(grade.tradename).join("") }));

  const exactGrades = gradeKeys.filter((entry) => entry.key === fileKey).map((entry) => entry.grade);
  if (exactGrades.length) return singleOrPickOne("exact", narrowBySupplier(exactGrades, fileKey));

  const gradesInFileName = gradeKeys.filter(
    (entry) => entry.key.length >= SHORTEST_NAME_TO_FIND_IN_FILE && fileKey.includes(entry.key),
  );
  if (gradesInFileName.length) {
    const longestKeyLength = Math.max(...gradesInFileName.map((entry) => entry.key.length));
    const longestGrades = gradesInFileName
      .filter((entry) => entry.key.length === longestKeyLength)
      .map((entry) => entry.grade);
    return singleOrPickOne("nameInFile", narrowBySupplier(longestGrades, fileKey));
  }

  const gradesContainingFileName = gradeKeys.filter(
    (entry) => fileKey.length >= SHORTEST_NAME_FOR_CLOSE_MATCH && entry.key.includes(fileKey),
  );
  if (gradesContainingFileName.length) {
    const shortestKeyLength = Math.min(...gradesContainingFileName.map((entry) => entry.key.length));
    const shortestGrades = gradesContainingFileName
      .filter((entry) => entry.key.length === shortestKeyLength)
      .map((entry) => entry.grade);
    return singleOrPickOne("fileNameInGrade", narrowBySupplier(shortestGrades, fileKey));
  }

  const closeGrades = gradeKeys
    .map((entry) => ({
      grade: entry.grade,
      distance: editDistance(fileKey, entry.key),
      longerLength: Math.max(fileKey.length, entry.key.length),
    }))
    .filter(
      (entry) =>
        entry.longerLength >= SHORTEST_NAME_FOR_CLOSE_MATCH &&
        entry.distance <= Math.max(1, Math.floor(entry.longerLength * CLOSE_MATCH_ALLOWED_SHARE)),
    );
  if (closeGrades.length) {
    const smallestDistance = Math.min(...closeGrades.map((entry) => entry.distance));
    const nearestGrades = closeGrades.filter((entry) => entry.distance === smallestDistance).map((entry) => entry.grade);
    return singleOrPickOne("close", nearestGrades);
  }

  return { status: "noMatch", candidates: [] };
}

export type PdfCheck = "confirmed" | "foundInPdf" | "otherGradeInPdf" | "notInPdf";

export interface PdfCheckResult {
  pdfCheck: PdfCheck | null;
  gradeId: number | null;
  gradesInPdf: MatchableGrade[];
}

export function gradesNamedInText(pageText: string, grades: MatchableGrade[]): MatchableGrade[] {
  const textKey = wordsOf(pageText).join("");
  if (!textKey) return [];
  const gradesInText = grades
    .map((grade) => ({ grade, key: wordsOf(grade.tradename).join("") }))
    .filter((entry) => entry.key.length >= SHORTEST_NAME_FOR_CLOSE_MATCH && textKey.includes(entry.key));
  if (!gradesInText.length) return [];
  const longestKeyLength = Math.max(...gradesInText.map((entry) => entry.key.length));
  const longestGrades = gradesInText.filter((entry) => entry.key.length === longestKeyLength).map((entry) => entry.grade);
  return narrowBySupplier(longestGrades, textKey);
}

export function checkWithPdf(
  match: TdsMatch,
  chosenGradeId: number | null,
  pageText: string,
  grades: MatchableGrade[],
): PdfCheckResult {
  const gradesInPdf = gradesNamedInText(pageText, grades);
  const unchanged = { gradeId: chosenGradeId, gradesInPdf };
  if (chosenGradeId !== null && gradesInPdf.some((grade) => grade.gradeId === chosenGradeId)) {
    return { ...unchanged, pdfCheck: "confirmed" };
  }
  if (chosenGradeId === null && gradesInPdf.length === 1) {
    return { pdfCheck: "foundInPdf", gradeId: gradesInPdf[0].gradeId, gradesInPdf };
  }
  if (chosenGradeId !== null && gradesInPdf.length > 0) return { ...unchanged, pdfCheck: "otherGradeInPdf" };
  if (match.status === "close" && wordsOf(pageText).length > 0) return { ...unchanged, pdfCheck: "notInPdf" };
  return { ...unchanged, pdfCheck: null };
}
